import { getDateEndOfDay, getDateStartOfDay } from "@/utils/dates";

export type QueryFilter = [column: string, operator: string, value: unknown];

interface MakeQueryFilterInParams<T, K extends keyof T> {
  items: T[];
  filterKey: string;
  byItemKey?: K;
  noShortCircuit?: boolean;
}
export const makeQueryFilterIn = <T, K extends keyof T>({
  items,
  filterKey,
  byItemKey,
  noShortCircuit = false,
}: MakeQueryFilterInParams<T, K>): QueryFilter | undefined => {
  if (items.length <= 0) {
    if (noShortCircuit) {
      return [filterKey, "in", `()`] as QueryFilter;
    }
    return undefined;
  }
  let queryFilter: QueryFilter | undefined = undefined;
  if (byItemKey) {
    queryFilter = [filterKey, "in", `(${items.map((item) => item[byItemKey]).join(",")})`];
  } else {
    queryFilter = [filterKey, "in", `(${items.join(",")})`];
  }
  return queryFilter;
};

interface MakeQueryFilterInJSONParams<T, K extends keyof T> {
  items: T[];
  filterObjectKey: string;
  filterKey: string;
  byItemKey?: K;
  noShortCircuit?: boolean;
}
export const makeQueryFilterInJSON = <T, K extends keyof T>({
  items,
  filterObjectKey,
  filterKey,
  byItemKey,
  noShortCircuit = false,
}: MakeQueryFilterInJSONParams<T, K>): QueryFilter | undefined => {
  if (items.length <= 0) {
    if (noShortCircuit) {
      return [filterObjectKey, "cs", `[]`] as QueryFilter;
    }
    return undefined;
  }
  let queryFilter: QueryFilter | undefined = undefined;
  if (byItemKey) {
    queryFilter = [filterObjectKey, "cs", `[{"${filterKey}":"${items.map((item) => item[byItemKey]).join(",")}}]`];
  } else {
    queryFilter = [filterObjectKey, "cs", `[{"${filterKey}":"${items.join(",")}"}]`];
  }
  return queryFilter;
};

interface MakeQueryFilterInArray<T, K extends keyof T> {
  items: T[];
  filterKey: string;
  byItemKey?: K;
  noShortCircuit?: boolean;
}
export const makeQueryFilterInArray = <T, K extends keyof T>({
  items,
  filterKey,
  byItemKey,
  noShortCircuit = false,
}: MakeQueryFilterInArray<T, K>): QueryFilter | undefined => {
  if (items.length <= 0) {
    if (noShortCircuit) {
      return [filterKey, "cs", `{}`] as QueryFilter;
    }
    return undefined;
  }
  let queryFilter: QueryFilter | undefined = undefined;
  if (byItemKey) {
    queryFilter = [filterKey, "cs", `{${items.map((item) => item[byItemKey]).join(",")}}`];
  } else {
    queryFilter = [filterKey, "cs", `{${items.join(",")}}`];
  }
  return queryFilter;
};

interface MakeQueryFilterNotInParams<T, K extends keyof T> {
  items: T[];
  filterKey: string;
  byItemKey?: K;
}
export const makeQueryFilterNotIn = <T, K extends keyof T>({
  items,
  filterKey,
  byItemKey,
}: MakeQueryFilterNotInParams<T, K>): QueryFilter | undefined => {
  if (items.length <= 0) return undefined;
  let queryFilter: QueryFilter | undefined = undefined;
  if (byItemKey) {
    queryFilter = [filterKey, "not.in", `(${items.map((item) => item[byItemKey]).join(",")})`];
  } else {
    queryFilter = [filterKey, "not.in", `(${items.join(",")})`];
  }
  return queryFilter;
};

// used for or-filters
export const makeDottedQueryFilter = (filter: QueryFilter | undefined) => {
  if (!filter) return;
  return filter.join(".");
};

export const makeBetweenDatesQueryFilter = (
  field: string,
  from?: Date | string | null,
  until?: Date | string | null,
): QueryFilter[] => {
  if (!from || !until || !field) return [];
  const start_date = getDateStartOfDay(from);
  const end_date = getDateEndOfDay(until);
  return [
    [field, "gt", start_date],
    [field, "lt", end_date],
  ];
};
